import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  useTheme,
  SelectChangeEvent,
  Chip
} from '@mui/material';
import {
  Add,
  Search,
  FilterList,
  Sort,
  Clear,
  AutoAwesome
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import TaskCard from './TaskCard';
import TaskForm from './TaskForm';
import TaskDetail from './TaskDetail';
import { Task, TaskCreate, TaskUpdate } from '../../types/task';
import { getTasks, createTask, updateTask, deleteTask, summarizeTask } from '../../services/taskService';

const TaskList: React.FC = () => {
  const theme = useTheme();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('due_date');
  const [formOpen, setFormOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState<Task | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [summary, setSummary] = useState({ open: false, text: '', loading: false });
  const [detailView, setDetailView] = useState<{ open: boolean, task: Task | null }>({ open: false, task: null });

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterAndSortTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, searchQuery, statusFilter, priorityFilter, sortBy]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const data = await getTasks();
      setTasks(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch tasks');
      showSnackbar('Failed to load tasks', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortTasks = () => {
    let filtered = [...tasks];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(task =>
        task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(task => task.status === statusFilter);
    }

    // Apply priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(task => task.priority === priorityFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'due_date':
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;
          return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
        case 'priority':
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          return priorityOrder[a.priority as keyof typeof priorityOrder] -
            priorityOrder[b.priority as keyof typeof priorityOrder];
        case 'title':
          return a.title.localeCompare(b.title);
        case 'created_at':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        default:
          return 0;
      }
    });

    setFilteredTasks(filtered);
  };

  const handleCreateTask = async (taskData: TaskCreate) => {
    try {
      const newTask = await createTask(taskData);
      setTasks(prev => [...prev, newTask]);
      showSnackbar('Task created successfully', 'success');
    } catch (err: any) {
      showSnackbar('Failed to create task', 'error');
    }
  };

  const handleUpdateTask = async (taskData: TaskUpdate) => {
    if (!currentTask) return;

    try {
      const updatedTask = await updateTask(currentTask.id, taskData);
      setTasks(prev => prev.map(task => task.id === updatedTask.id ? updatedTask : task));
      showSnackbar('Task updated successfully', 'success');
    } catch (err: any) {
      showSnackbar('Failed to update task', 'error');
    }
  };

  const handleDeleteTask = async () => {
    if (taskToDelete === null) return;

    try {
      await deleteTask(taskToDelete);
      setTasks(prev => prev.filter(task => task.id !== taskToDelete));
      showSnackbar('Task deleted successfully', 'success');
    } catch (err: any) {
      showSnackbar('Failed to delete task', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
    }
  };

  const handleStatusChange = async (id: number, completed: boolean) => {
    try {
      // Find the current task
      const taskToUpdate = tasks.find(task => task.id === id);
      if (!taskToUpdate) return;
      
      // Update both the completed flag and the status field for consistency
      const updatedTask = await updateTask(id, {
        completed,
        status: completed ? 'completed' : taskToUpdate.status === 'due-soon' ? 'due-soon' : 'in-progress'
      });
      
      // Update the tasks state to trigger re-render of all sections
      setTasks(prev => {
        const newTasks = prev.map(task => task.id === updatedTask.id ? updatedTask : task);
        // Force re-filtering to update section counts
        setTimeout(() => filterAndSortTasks(), 0);
        return newTasks;
      });
      
      showSnackbar(`Task marked as ${completed ? 'completed' : 'in progress'}`, 'success');
    } catch (err: any) {
      showSnackbar('Failed to update task status', 'error');
    }
  };

  const handleSummarize = async (description: string) => {
    setSummary({ ...summary, open: true, loading: true });
    try {
      const response = await summarizeTask({ description });
      setSummary({ open: true, text: response.summary, loading: false });
    } catch (err: any) {
      setSummary({ open: true, text: 'Failed to generate summary', loading: false });
    }
  };

  const handleOpenForm = (task?: Task) => {
    setCurrentTask(task);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setCurrentTask(undefined);
  };

  const handleSubmitForm = (taskData: TaskCreate | TaskUpdate) => {
    if (currentTask) {
      handleUpdateTask(taskData);
    } else {
      handleCreateTask(taskData as TaskCreate);
    }
  };

  const handleOpenDeleteDialog = (id: number) => {
    setTaskToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleStatusFilterChange = (e: SelectChangeEvent<string>) => {
    setStatusFilter(e.target.value);
  };

  const handlePriorityFilterChange = (e: SelectChangeEvent<string>) => {
    setPriorityFilter(e.target.value);
  };

  const handleSortChange = (e: SelectChangeEvent<string>) => {
    setSortBy(e.target.value);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPriorityFilter('all');
    setSortBy('due_date');
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCloseSummary = () => {
    setSummary({ ...summary, open: false });
  };
  
  const handleOpenTaskDetail = (task: Task) => {
    // First close any existing dialog to reset state
    setDetailView({ open: false, task: null });
    
    // Then open the new task detail after a short delay
    setTimeout(() => {
      setDetailView({ open: true, task });
    }, 50);
  };
  
  const handleCloseTaskDetail = () => {
    // First close the dialog
    setDetailView(prev => ({ ...prev, open: false }));
    
    // Then clear the task after a short delay to allow the closing animation to complete
    setTimeout(() => {
      setDetailView({ open: false, task: null });
    }, 300);
  };

  return (
    <Box sx={{ py: 3 }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 4,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #00e5ff, #2979ff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Task Manager
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => handleOpenForm()}
          sx={{
            background: 'linear-gradient(45deg, #00e5ff 30%, #2979ff 90%)',
            boxShadow: '0 0 10px rgba(0, 229, 255, 0.5)',
            '&:hover': {
              boxShadow: '0 0 20px rgba(0, 229, 255, 0.8)',
            }
          }}
        >
          Add New Task
        </Button>
      </Box>

      <Box sx={{
        mb: 4,
        p: 2,
        borderRadius: '16px',
        backgroundColor: 'rgba(30, 30, 30, 0.6)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={handleSearchChange}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
                sx: {
                  borderRadius: '8px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    boxShadow: '0 0 0 2px rgba(0, 229, 255, 0.2)',
                  }
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="status-filter-label">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FilterList fontSize="small" sx={{ mr: 0.5 }} />
                  Status
                </Box>
              </InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                label="Status"
                sx={{
                  borderRadius: '8px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    boxShadow: '0 0 0 2px rgba(0, 229, 255, 0.2)',
                  }
                }}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="todo">To Do</MenuItem>
                <MenuItem value="in-progress">In Progress</MenuItem>
                <MenuItem value="due-soon">Due Soon</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="priority-filter-label">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FilterList fontSize="small" sx={{ mr: 0.5 }} />
                  Priority
                </Box>
              </InputLabel>
              <Select
                labelId="priority-filter-label"
                value={priorityFilter}
                onChange={handlePriorityFilterChange}
                label="Priority"
                sx={{
                  borderRadius: '8px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    boxShadow: '0 0 0 2px rgba(0, 229, 255, 0.2)',
                  }
                }}
              >
                <MenuItem value="all">All Priorities</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="sort-by-label">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Sort fontSize="small" sx={{ mr: 0.5 }} />
                  Sort By
                </Box>
              </InputLabel>
              <Select
                labelId="sort-by-label"
                value={sortBy}
                onChange={handleSortChange}
                label="Sort By"
                sx={{
                  borderRadius: '8px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    boxShadow: '0 0 0 2px rgba(0, 229, 255, 0.2)',
                  }
                }}
              >
                <MenuItem value="due_date">Due Date</MenuItem>
                <MenuItem value="priority">Priority</MenuItem>
                <MenuItem value="title">Title</MenuItem>
                <MenuItem value="created_at">Created Date</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={clearFilters}
              startIcon={<Clear />}
              disabled={!searchQuery && statusFilter === 'all' && priorityFilter === 'all' && sortBy === 'due_date'}
              sx={{
                height: '56px',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                }
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>

        {(searchQuery || statusFilter !== 'all' || priorityFilter !== 'all') && (
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
              Active filters:
            </Typography>

            {searchQuery && (
              <Chip
                label={`Search: ${searchQuery}`}
                onDelete={() => setSearchQuery('')}
                size="small"
                sx={{
                  backgroundColor: `${theme.palette.primary.main}20`,
                  color: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}50`,
                }}
              />
            )}

            {statusFilter !== 'all' && (
              <Chip
                label={`Status: ${statusFilter}`}
                onDelete={() => setStatusFilter('all')}
                size="small"
                sx={{
                  backgroundColor: `${theme.palette.info.main}20`,
                  color: theme.palette.info.main,
                  border: `1px solid ${theme.palette.info.main}50`,
                }}
              />
            )}

            {priorityFilter !== 'all' && (
              <Chip
                label={`Priority: ${priorityFilter}`}
                onDelete={() => setPriorityFilter('all')}
                size="small"
                sx={{
                  backgroundColor: `${theme.palette.warning.main}20`,
                  color: theme.palette.warning.main,
                  border: `1px solid ${theme.palette.warning.main}50`,
                }}
              />
            )}
          </Box>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress size={60} sx={{ color: theme.palette.primary.main }} />
        </Box>
      ) : error ? (
        <Box sx={{
          textAlign: 'center',
          py: 8,
          px: 2,
          borderRadius: '16px',
          backgroundColor: 'rgba(30, 30, 30, 0.6)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 0, 0, 0.2)',
        }}>
          <Typography variant="h6" color="error" gutterBottom>
            {error}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={fetchTasks}
            sx={{ mt: 2 }}
          >
            Try Again
          </Button>
        </Box>
      ) : filteredTasks.length === 0 ? (
        <Box sx={{
          textAlign: 'center',
          py: 8,
          px: 2,
          borderRadius: '16px',
          backgroundColor: 'rgba(30, 30, 30, 0.6)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <Typography variant="h6" gutterBottom>
            {tasks.length === 0
              ? "You don't have any tasks yet"
              : "No tasks match your filters"}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {tasks.length === 0
              ? "Let's create your first task to get started"
              : "Try adjusting your search or filters"}
          </Typography>
          {tasks.length === 0 ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => handleOpenForm()}
              sx={{
                background: 'linear-gradient(45deg, #00e5ff 30%, #2979ff 90%)',
                boxShadow: '0 0 10px rgba(0, 229, 255, 0.5)',
                '&:hover': {
                  boxShadow: '0 0 20px rgba(0, 229, 255, 0.8)',
                }
              }}
            >
              Create First Task
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Clear />}
              onClick={clearFilters}
              sx={{
                borderColor: theme.palette.primary.main,
                '&:hover': {
                  borderColor: theme.palette.primary.light,
                  backgroundColor: 'rgba(0, 229, 255, 0.1)',
                }
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      ) : (
        <AnimatePresence>
          {/* Due Soon Section */}
          {(() => {
            // Get tasks due within the next 3 days
            const today = new Date();
            const threeDaysFromNow = new Date();
            threeDaysFromNow.setDate(today.getDate() + 3);
            
            const dueSoonTasks = filteredTasks.filter(task => {
              if (!task.due_date || task.completed) return false;
              const dueDate = new Date(task.due_date);
              return dueDate >= today && dueDate <= threeDaysFromNow;
            });
            
            if (dueSoonTasks.length > 0) {
              return (
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 2,
                    pb: 1,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: theme.palette.warning.main,
                        fontWeight: 'bold'
                      }}
                    >
                      Due Soon
                    </Typography>
                    <Chip 
                      label={dueSoonTasks.length} 
                      size="small" 
                      sx={{ 
                        ml: 2,
                        backgroundColor: `${theme.palette.warning.main}20`,
                        color: theme.palette.warning.main,
                        border: `1px solid ${theme.palette.warning.main}50`,
                      }} 
                    />
                  </Box>
                  <Grid container spacing={3}>
                    {dueSoonTasks.map((task) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={task.id}>
                        <motion.div
                          key={task.id}
                          layout
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <TaskCard
                            task={task}
                            onEdit={handleOpenForm}
                            onDelete={handleOpenDeleteDialog}
                            onStatusChange={handleStatusChange}
                            onSummarize={handleSummarize}
                            onViewDetails={handleOpenTaskDetail}
                          />
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            }
            return null;
          })()}
          
          {/* Completed Tasks Section */}
          {(() => {
            const completedTasks = filteredTasks.filter(task => task.completed);
            
            if (completedTasks.length > 0) {
              return (
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 2,
                    pb: 1,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: theme.palette.success.main,
                        fontWeight: 'bold'
                      }}
                    >
                      Completed
                    </Typography>
                    <Chip 
                      label={completedTasks.length} 
                      size="small" 
                      sx={{ 
                        ml: 2,
                        backgroundColor: `${theme.palette.success.main}20`,
                        color: theme.palette.success.main,
                        border: `1px solid ${theme.palette.success.main}50`,
                      }} 
                    />
                  </Box>
                  <Grid container spacing={3}>
                    {completedTasks.map((task) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={task.id}>
                        <motion.div
                          key={task.id}
                          layout
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <TaskCard
                            task={task}
                            onEdit={handleOpenForm}
                            onDelete={handleOpenDeleteDialog}
                            onStatusChange={handleStatusChange}
                            onSummarize={handleSummarize}
                            onViewDetails={handleOpenTaskDetail}
                          />
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            }
            return null;
          })()}
          
          {/* All Other Tasks */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              pb: 1,
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: theme.palette.primary.main,
                  fontWeight: 'bold'
                }}
              >
                All Tasks
              </Typography>
              <Chip 
                label={filteredTasks.filter(task => !task.completed).length} 
                size="small" 
                sx={{ 
                  ml: 2,
                  backgroundColor: `${theme.palette.primary.main}20`,
                  color: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}50`,
                }} 
              />
            </Box>
            <Grid container spacing={3}>
              {filteredTasks
                .filter(task => !task.completed && !(
                  task.due_date && 
                  (() => {
                    const today = new Date();
                    const threeDaysFromNow = new Date();
                    threeDaysFromNow.setDate(today.getDate() + 3);
                    const dueDate = new Date(task.due_date);
                    return dueDate >= today && dueDate <= threeDaysFromNow;
                  })()
                ))
                .map((task) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={task.id}>
                  <motion.div
                    key={task.id}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <TaskCard
                      task={task}
                      onEdit={handleOpenForm}
                      onDelete={handleOpenDeleteDialog}
                      onStatusChange={handleStatusChange}
                      onSummarize={handleSummarize}
                      onViewDetails={handleOpenTaskDetail}
                    />
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>
        </AnimatePresence>
      )}

      {/* Floating action button for mobile */}
      <Box sx={{ display: { sm: 'none' } }}>
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => handleOpenForm()}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            background: 'linear-gradient(45deg, #00e5ff 30%, #2979ff 90%)',
            boxShadow: '0 0 10px rgba(0, 229, 255, 0.5)',
            '&:hover': {
              boxShadow: '0 0 20px rgba(0, 229, 255, 0.8)',
            }
          }}
        >
          <Add />
        </Fab>
      </Box>

      {/* Task form dialog */}
      <TaskForm
        open={formOpen}
        onClose={handleCloseForm}
        onSubmit={handleSubmitForm}
        task={currentTask}
        title={currentTask ? 'Edit Task' : 'Create New Task'}
      />

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            background: 'rgba(30, 30, 30, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText>
            Are you sure you want to delete this task? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteTask}
            color="error"
            variant="contained"
            sx={{
              bgcolor: theme.palette.error.main,
              '&:hover': {
                bgcolor: theme.palette.error.dark,
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Summary dialog */}
      <Dialog
        open={summary.open}
        onClose={handleCloseSummary}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            background: 'rgba(30, 30, 30, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <AutoAwesome sx={{ color: theme.palette.secondary.main }} />
          <Typography
            variant="h6"
            component="span"
            sx={{
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #ff00e5, #ff3d00)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            AI Summary
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {summary.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress size={40} sx={{ color: theme.palette.secondary.main }} />
            </Box>
          ) : (
            <Typography variant="body1">
              {summary.text}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Button
            onClick={handleCloseSummary}
            variant="outlined"
            sx={{
              borderColor: theme.palette.secondary.main,
              color: theme.palette.secondary.main,
              '&:hover': {
                borderColor: theme.palette.secondary.light,
                backgroundColor: 'rgba(255, 0, 229, 0.1)',
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Detail View */}
      <TaskDetail
        task={detailView.task}
        open={detailView.open}
        onClose={handleCloseTaskDetail}
        onEdit={handleOpenForm}
        onDelete={handleOpenDeleteDialog}
        onStatusChange={handleStatusChange}
      />
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TaskList;